import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Location } from "@/data/mockWarehousesLocations";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";

interface BulkCreateLocationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouseCode: string;
  existingLocations: Location[];
  onComplete: (locations: Location[]) => void;
  isLive?: boolean;
}

export function BulkCreateLocationsDialog({ 
  open, 
  onOpenChange, 
  warehouseCode, 
  existingLocations,
  onComplete,
  isLive = false
}: BulkCreateLocationsDialogProps) {
  const hierarchyConfig = useWarehouseHierarchyConfig({ isLive });
  const [mode, setMode] = useState<"range" | "grid">("range");
  
  // Range mode state
  const [rangePrefix, setRangePrefix] = useState("A");
  const [rangeStart, setRangeStart] = useState("1");
  const [rangeEnd, setRangeEnd] = useState("10");
  const [rangeSuffix, setRangeSuffix] = useState("");
  
  // Grid mode state - dynamic based on active levels (excluding level 1 which is warehouse)
  const gridLevels = hierarchyConfig.activeLevels.filter(l => l.level > 1);
  const [gridCounts, setGridCounts] = useState<{ [key: number]: string }>(() => {
    const initial: { [key: number]: string } = {};
    gridLevels.forEach((level, index) => {
      initial[level.level] = index === 0 ? "3" : index === 1 ? "5" : index === 2 ? "4" : "2";
    });
    return initial;
  });
  
  // Common
  const [locationType, setLocationType] = useState<Location["locationType"]>("Storage");
  
  const [previewLocations, setPreviewLocations] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const generateRangePreview = () => {
    const locations: string[] = [];
    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);
    
    if (isNaN(start) || isNaN(end) || start > end || end - start > 100) {
      return [];
    }
    
    for (let i = start; i <= end; i++) {
      const code = `${rangePrefix}${String(i).padStart(2, '0')}${rangeSuffix ? `-${rangeSuffix}` : ""}`;
      locations.push(code);
    }
    
    return locations;
  };

  const generateGridPreview = () => {
    const locations: string[] = [];
    
    // Parse counts for each level
    const counts = gridLevels.map(level => parseInt(gridCounts[level.level] || "0"));
    
    // Validate all counts
    if (counts.some(c => isNaN(c) || c <= 0)) {
      return [];
    }
    
    // Calculate total combinations
    const totalCombinations = counts.reduce((acc, count) => acc * count, 1);
    if (totalCombinations > 500) {
      return [];
    }
    
    // Generate all combinations recursively
    const generateCombinations = (levelIndex: number, currentParts: string[]): void => {
      if (levelIndex >= gridLevels.length) {
        locations.push(currentParts.join('-'));
        return;
      }
      
      const level = gridLevels[levelIndex];
      const count = counts[levelIndex];
      
      for (let i = 1; i <= count; i++) {
        const prefix = level.codePrefix.trim();
        const part = prefix ? `${prefix}${String(i).padStart(2, '0')}` : String(i).padStart(2, '0');
        generateCombinations(levelIndex + 1, [...currentParts, part]);
      }
    };
    
    generateCombinations(0, []);
    return locations;
  };

  const handlePreview = () => {
    const preview = mode === "range" ? generateRangePreview() : generateGridPreview();
    setPreviewLocations(preview);
    setShowPreview(true);
  };

  const handleCreate = () => {
    const codes = mode === "range" ? generateRangePreview() : generateGridPreview();
    
    const newLocations: Location[] = codes.map((code, index) => {
      // Parse hierarchy from code
      const parts = code.split("-");
      const hierarchyData: { [key: number]: string } = {};
      
      // Map parts to hierarchy levels
      gridLevels.forEach((level, idx) => {
        if (parts[idx]) {
          hierarchyData[level.level] = parts[idx];
        }
      });
      
      // Map to legacy fields for backward compatibility
      const aisle = hierarchyData[2];
      const rack = hierarchyData[3];
      const shelf = hierarchyData[4];
      const bin = hierarchyData[5];
      
      return {
        locationCode: code,
        warehouseCode,
        hierarchyData,
        aisle,
        rack,
        shelf,
        bin,
        locationType,
        currentStock: 0,
        utilizationPercent: 0,
        pickingSequence: index + 1,
        active: true,
      };
    });
    
    onComplete(newLocations);
    onOpenChange(false);
    
    // Reset form
    setShowPreview(false);
    setPreviewLocations([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Create Locations</DialogTitle>
          <DialogDescription>
            Create multiple locations at once using range or grid patterns
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as "range" | "grid")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="range">Create Range</TabsTrigger>
            <TabsTrigger value="grid">Create Grid</TabsTrigger>
          </TabsList>

          <TabsContent value="range" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rangePrefix">Prefix</Label>
                  <Input
                    id="rangePrefix"
                    value={rangePrefix}
                    onChange={(e) => setRangePrefix(e.target.value.toUpperCase())}
                    placeholder="e.g., A"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rangeSuffix">Suffix (Optional)</Label>
                  <Input
                    id="rangeSuffix"
                    value={rangeSuffix}
                    onChange={(e) => setRangeSuffix(e.target.value.toUpperCase())}
                    placeholder="e.g., MAIN"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rangeStart">Start Number</Label>
                  <Input
                    id="rangeStart"
                    type="number"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rangeEnd">End Number</Label>
                  <Input
                    id="rangeEnd"
                    type="number"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="p-3 bg-muted rounded-md text-sm">
                <p className="font-medium mb-1">Example:</p>
                <p className="text-muted-foreground">
                  Prefix "A", Start 1, End 10, Suffix "MAIN" → A01-MAIN, A02-MAIN, ..., A10-MAIN
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="grid" className="space-y-4 mt-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Define how many locations to create at each hierarchy level. The system will generate all combinations.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {gridLevels.map((level, index) => {
                  const label = index === 0 
                    ? `Number of ${level.displayName}s`
                    : `${level.displayName}s per ${gridLevels[index - 1].displayName}`;
                  
                  return (
                    <div key={level.level} className="space-y-2">
                      <Label htmlFor={`level${level.level}Count`}>{label}</Label>
                      <Input
                        id={`level${level.level}Count`}
                        type="number"
                        value={gridCounts[level.level] || ""}
                        onChange={(e) => setGridCounts({ ...gridCounts, [level.level]: e.target.value })}
                        min="1"
                        max="20"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="p-3 bg-muted rounded-md text-sm">
                <p className="font-medium mb-1">Total locations to create:</p>
                <p className="text-lg font-bold">
                  {gridLevels.reduce((acc, level) => acc * parseInt(gridCounts[level.level] || "0"), 1)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum 500 locations per bulk create
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Common fields */}
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="locationType">Location Type</Label>
            <Select value={locationType} onValueChange={(v) => setLocationType(v as Location["locationType"])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Storage">Storage</SelectItem>
                <SelectItem value="Picking">Picking</SelectItem>
                <SelectItem value="Receiving">Receiving</SelectItem>
                <SelectItem value="Staging">Staging</SelectItem>
                <SelectItem value="Shipping">Shipping</SelectItem>
                <SelectItem value="Quarantine">Quarantine</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview */}
        {showPreview && previewLocations.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <div className="flex items-center justify-between">
              <Label>Preview ({previewLocations.length} locations)</Label>
              <Badge variant="outline">{previewLocations.length} total</Badge>
            </div>
            <ScrollArea className="h-[200px] rounded-md border p-4">
              <div className="space-y-1">
                {previewLocations.map((code, index) => (
                  <div key={index} className="text-sm font-mono">
                    {code}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!showPreview ? (
            <Button onClick={handlePreview}>
              Preview
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={previewLocations.length === 0}>
              Create {previewLocations.length} Locations
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
